import {useState, type DragEvent} from 'react';
import {AwErrorState, AwFilterBar, AwLoadingState, AwPanel, AwSelect, AwWarTableRoute} from '@ascension-tracker/ascension-ui';
import {TaskRow} from '../../components/TaskRow.js';
import {QuickAddTask} from '../../components/QuickAddTask.js';
import {useDialogs} from '../../components/DialogProvider.js';
import {ApiClientError} from '../../api.js';
import {useCreateTask, useMoveTask, useProjectDashboard, useProjectReleases, useProjectSections, useProjectTasks, useToggleTaskComplete, useUpdateTaskFields, type Section, type Task, type TaskEnvironment, type TaskPriority} from '../../hooks/useTasks.js';

const priorityOptions: TaskPriority[] = ['critical', 'high', 'medium', 'low'];
const environmentOptions: TaskEnvironment[] = ['development', 'testing', 'integration', 'early_access', 'production'];
const workflowDotClass: Record<Section['workflowState'], string> = {todo: 'is-todo', in_progress: 'is-in-progress', done: 'is-done'};

export function ListView({projectId, projectKey, onOpenTask, filtersOpen}: {projectId: string; projectKey: string; onOpenTask: (taskId: string) => void; filtersOpen: boolean}) {
  const sections = useProjectSections(projectId);
  const tasks = useProjectTasks(projectId);
  const error = (sections.error as Error | null)?.message || (tasks.error as Error | null)?.message;
  if (error) return <AwErrorState>{error}</AwErrorState>;
  if (!sections.data || !tasks.data) return <AwLoadingState/>;
  return <ListViewBody projectId={projectId} projectKey={projectKey} sections={sections.data} tasks={tasks.data} onOpenTask={onOpenTask} filtersOpen={filtersOpen}/>;
}

type Group = {id: string; name: string; workflowState: Section['workflowState']; backlog?: boolean};

function ListViewBody({projectId, projectKey, sections, tasks, onOpenTask, filtersOpen}: {projectId: string; projectKey: string; sections: Section[]; tasks: Task[]; onOpenTask: (taskId: string) => void; filtersOpen: boolean}) {
  const {alert} = useDialogs();
  const createTask = useCreateTask(projectId);
  const toggleComplete = useToggleTaskComplete(projectId);
  const moveTask = useMoveTask(projectId);
  const updateFields = useUpdateTaskFields(projectId);
  const releases = useProjectReleases(projectId);
  const dashboard = useProjectDashboard(projectKey);
  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null);
  const [collapsedTasks, setCollapsedTasks] = useState<Set<string>>(new Set());
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [releaseFilter, setReleaseFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [environmentFilter, setEnvironmentFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');

  const defaultSection = sections.find(section => section.isDefault) ?? null;
  const backlogSection = sections.find(section => section.isBacklog) ?? null;
  const assignees = Array.from(new Set(tasks.map(task => task.assigneeName).filter((name): name is string => Boolean(name))));

  function toggleCollapse(taskId: string) {
    setCollapsedTasks(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId); else next.add(taskId);
      return next;
    });
  }
  function toggleSectionCollapse(sectionId: string) {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId); else next.add(sectionId);
      return next;
    });
  }

  function move(taskId: string, sectionId: string | null, beforeTaskId?: string | null, afterTaskId?: string | null) {
    moveTask.mutate({taskId, sectionId, beforeTaskId, afterTaskId}, {onError: error => { void alert(error instanceof ApiClientError ? error.message : 'Could not move task'); }});
  }

  function dropOnGroup(event: DragEvent, groupId: string, groupTasks: Task[]) {
    event.preventDefault();
    if (!draggedTaskId) return;
    const last = groupTasks[groupTasks.length - 1];
    move(draggedTaskId, groupId, last ? last.id : undefined, undefined);
  }
  function dropOnRow(event: DragEvent, groupId: string, groupTasks: Task[], index: number) {
    event.preventDefault();
    event.stopPropagation();
    if (!draggedTaskId) return;
    const before = index > 0 ? groupTasks[index - 1] : undefined;
    const after = groupTasks[index];
    if (draggedTaskId === after?.id) return;
    move(draggedTaskId, groupId, before?.id, after?.id);
  }

  const filteredTasks = tasks.filter(task =>
    (!releaseFilter || task.releaseId === releaseFilter) &&
    (!priorityFilter || task.priority === priorityFilter) &&
    (!environmentFilter || task.environment === environmentFilter) &&
    (!assigneeFilter || task.assigneeName === assigneeFilter)
  );

  const childrenByParent = new Map<string, Task[]>();
  for (const task of filteredTasks) if (task.parentTaskId) childrenByParent.set(task.parentTaskId, [...(childrenByParent.get(task.parentTaskId) ?? []), task]);

  const topLevel = filteredTasks.filter(task => !task.parentTaskId);
  const bySection = new Map<string, Task[]>();
  for (const task of topLevel) {
    const key = task.sectionId ?? defaultSection?.id;
    if (!key) continue;
    bySection.set(key, [...(bySection.get(key) ?? []), task]);
  }

  const boardSections = sections.filter(section => !section.isBacklog);
  const groups: Group[] = [
    ...boardSections.map(section => ({id: section.id, name: section.name, workflowState: section.workflowState})),
    ...(backlogSection ? [{id: backlogSection.id, name: backlogSection.name, workflowState: backlogSection.workflowState, backlog: true}] : [])
  ];

  const dashboardData = dashboard.data;
  const currentReleaseVersion = releases.data?.find(release => release.id === dashboardData?.project.currentReleaseId)?.version;
  const stageTiles = (dashboardData?.stages ?? []).map(stage => ({environment: stage.environment, state: stage.state as 'pending' | 'active' | 'completed' | 'failed'}));

  return (
    <div className="task-list">
      {dashboardData && (dashboardData.project.currentReleaseId || stageTiles.length > 0) && (
        <div className="task-list__context">
          <AwPanel className="task-list__release-focus">
            <h2>Current Release Focus</h2>
            {dashboardData.project.currentReleaseId ? (
              <>
                <p className="task-list__release-version">{currentReleaseVersion ?? 'Release in progress'}</p>
                <div className="task-list__release-stats">
                  <div><strong>{dashboardData.taskCounts.total ? Math.round((dashboardData.taskCounts.completed / dashboardData.taskCounts.total) * 100) : 0}%</strong><span>Completion</span></div>
                  <div><strong>{dashboardData.taskCounts.total}</strong><span>Tasks</span></div>
                  <div><strong>{dashboardData.blockers.length}</strong><span>Blockers</span></div>
                  <div><strong>{dashboardData.taskCounts.overdue}</strong><span>Overdue</span></div>
                </div>
                <a href={`/projects/${projectKey}/releases`}>View Release Details →</a>
              </>
            ) : <p className="task-detail__muted">No active release yet.</p>}
          </AwPanel>
          <AwPanel className="task-list__deploy-route">
            <h2>Deploy Route</h2>
            {stageTiles.length > 0 ? <AwWarTableRoute stages={stageTiles}/> : <p className="task-detail__muted">No release stages yet.</p>}
          </AwPanel>
        </div>
      )}

      {filtersOpen && (
        <AwFilterBar>
          <AwSelect id="list-filter-release" label="Release" value={releaseFilter} onChange={event => setReleaseFilter(event.target.value)}>
            <option value="">All</option>
            {releases.data?.map(release => <option key={release.id} value={release.id}>{release.version}</option>)}
          </AwSelect>
          <AwSelect id="list-filter-priority" label="Priority" value={priorityFilter} onChange={event => setPriorityFilter(event.target.value)}>
            <option value="">All</option>
            {priorityOptions.map(value => <option key={value} value={value}>{value}</option>)}
          </AwSelect>
          <AwSelect id="list-filter-environment" label="Environment" value={environmentFilter} onChange={event => setEnvironmentFilter(event.target.value)}>
            <option value="">All</option>
            {environmentOptions.map(value => <option key={value} value={value}>{value.replace('_', ' ')}</option>)}
          </AwSelect>
          <AwSelect id="list-filter-assignee" label="Assignee" value={assigneeFilter} onChange={event => setAssigneeFilter(event.target.value)}>
            <option value="">All</option>
            {assignees.map(name => <option key={name} value={name}>{name}</option>)}
          </AwSelect>
        </AwFilterBar>
      )}

      <div className="task-list__columns" aria-hidden="true">
        <span/>
        <span>Task</span>
        <span className="task-list__columns-fields">Priority</span>
        <span className="task-list__columns-fields">Environment</span>
        <span className="task-list__columns-fields">Release</span>
        <span>Assignee</span>
        <span>Due</span>
      </div>

      {groups.map(group => {
        const groupTasks = bySection.get(group.id) ?? [];
        const isCollapsed = collapsedSections.has(group.id);
        return (
          <AwPanel
            key={group.id}
            className={`task-section${group.backlog ? ' task-section--backlog' : ''}${draggedTaskId ? ' is-drop-target' : ''}`}
            onDragOver={event => event.preventDefault()}
            onDrop={event => dropOnGroup(event, group.id, groupTasks)}
          >
            <header className="task-section__header">
              <button type="button" className="task-section__collapse" aria-label={isCollapsed ? 'Expand section' : 'Collapse section'} aria-expanded={!isCollapsed} onClick={() => toggleSectionCollapse(group.id)}>{isCollapsed ? '›' : '⌄'}</button>
              <i className={`task-section__dot ${workflowDotClass[group.workflowState]}`} aria-hidden="true"/>
              <h2>{group.name}</h2>
              <span>{groupTasks.length}</span>
              {group.backlog && <span className="task-section__badge">Not on board</span>}
            </header>
            {!isCollapsed && <>
              {groupTasks.map((task, index) => {
                const children = childrenByParent.get(task.id) ?? [];
                const taskCollapsed = collapsedTasks.has(task.id);
                return (
                  <div key={task.id} onDragOver={event => event.preventDefault()} onDrop={event => dropOnRow(event, group.id, groupTasks, index)}>
                    <TaskRow
                      task={task}
                      onToggle={() => toggleComplete.mutate(task)}
                      onOpen={() => onOpenTask(task.id)}
                      draggable
                      onDragStart={() => setDraggedTaskId(task.id)}
                      onDragEnd={() => setDraggedTaskId(null)}
                      hasChildren={children.length > 0}
                      collapsed={taskCollapsed}
                      onToggleCollapse={() => toggleCollapse(task.id)}
                      releases={releases.data}
                      onUpdateFields={fields => updateFields.mutate({taskId: task.id, ...fields})}
                    />
                    {!taskCollapsed && children.map(child => (
                      <TaskRow key={child.id} task={child} onToggle={() => toggleComplete.mutate(child)} onOpen={() => onOpenTask(child.id)} indented/>
                    ))}
                  </div>
                );
              })}
              <QuickAddTask onAdd={title => createTask.mutateAsync({title, sectionId: group.id})}/>
            </>}
          </AwPanel>
        );
      })}
    </div>
  );
}
